import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import {Autocomplete} from '@mui/material';
import React from 'react';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import CircularProgress from '@mui/material/CircularProgress';
import {isEmpty} from 'lodash';

const filedStyle = makeStyles((theme) => ({
  textField: {
    width: '100%',
    borderRadius: 10,
    '& fieldset': {
      border: ({noBorder}) => (noBorder ? 'none' : ''),
      borderRadius: 6,
    }
  },
  font: {
    fontFamily: 'Inter',
    fontWeight: 500,
  },
  background: {
    backgroundColor: ({extended}) =>
      extended
        ? theme.palette.mode === 'light'
          ? '#ffffff'
          : '#333333'
        : theme.palette.background.field,
  },
  paperBackground: {
    backgroundColor: theme.palette.background.paper,
  },
  bigLabel: {
      fontSize: '1.15rem',
      // marginBottom: '2rem',
      '&.labelFocused': {
          color: 'purple',
      },
  },
  inPatientBackground: {
     backgroundColor: `${theme.palette.inPatient.inputBackground} !important`,
  },
}));

const AgyleDictionaryField = React.forwardRef((props, ref) => {
  const classes = filedStyle({
    noBorder: props.noBorder,
    extended: props.extended,
  });

  const equalValue = (option, value) => {
    if (props.isOptionEqualToValue) {
      return props.isOptionEqualToValue(option, value);
    } else {
      return option.__id === value.__id;
    }
  };

  const showNewLine = props.open && props.extended && !isEmpty(props.value);

  return (
    <Autocomplete
      options={props.options}
      multiple={props.multiple || props.isMultiple}
      value={props.value}
      inputValue={props.inputValue}
      color={props.color}
      openOnFocus={props.openOnFocus}
      size={props.size}
      disablePortal={props.disablePortal}
      onChange={props.onChange}
      onOpen={props.onOpen}
      onClose={props.onClose}
      onInputChange={props.onInputChange}
      limitTags={props.limitTags}
      getOptionDisabled={props.getOptionDisabled}
      getOptionLabel={(option) =>
        props.getOptionLabel
          ? props.getOptionLabel(option)
          : option.__description
      }
      onBlur={props.onBlur}
      loading={props.loading}
      disabled={props.disabled}
      disableCloseOnSelect={props.disableCloseOnSelect}
      required={props.required}
      disableClearable={props.disableClearable}
      readOnly={props.readOnly}
      classes={{
        paper: props.dropdownStyle ? props.dropdownStyle : classes.background,
        input: props.textFieldStyle, option: props.optionStyle
      }}
      ref={ref}
      style={{
        width: '100%',
        ...props.style,
      }}
      renderInput={(inp) => (
        <TextField
          {...inp}
          required={props.required}
          className={clsx(
            classes.textField,
            props.textFieldClass,
            props.withBackground && classes.background,
            props.paperBackground && classes.paperBackground,
            props.withNewBackground && classes.inPatientBackground,
          )}
          label={props.label}
          error={props.error}
          placeholder={props.placeholder}
          inputProps={{
            ...inp.inputProps,
            ...props.inputProps,
            readOnly: props.readOnly,
            // startAdornment: props.startIcon ? props.startIcon : inp.InputProps.startAdornment,
          }}
          sx={{
            '& .MuiAutocomplete-input': {
              minWidth: showNewLine ? `100% !important` : '',
            },
          }}
          InputProps={{
            ...props.inputProps,
            ...inp.InputProps,
            readOnly: props.readOnly,
            startAdornment: props.startIcon
              ? props.startIcon
              : props.startTextBoxAdornment
                ? props.startTextBoxAdornment()
                : inp.InputProps.startAdornment,
            // clearIcon: props.clearIcon ? props.clearIcon : inp.InputProps.clearIcon,
            endAdornment: props.endAdornment ? (
              props.endAdornment
            ) : (
              <>
                {props.endAdornmentAddons && props.endAdornmentAddons()}
                {props.loading ? (
                  <CircularProgress color="inherit" size={20}/>
                ) : null}
                {inp.InputProps.endAdornment}
              </>
            ),
          }}
          InputLabelProps={{
            classes: {
              root: clsx(
                props.inputLabelCalss,
                props.bigLabel && classes.bigLabel,
              ),
            },
            // shrink: props.shrink
          }}
          autoFocus={props.open}
          inputRef={(input) => {
            if (props.focusInput) props.focusInput(input);
          }}
        />
      )
      }
      ListboxProps={{
        className: 'scroll-normal-white',
      }}
      renderOption={props.renderLocalOption || props.renderOption}
      isOptionEqualToValue={equalValue}
      filterOptions={props.filterOptions}
      groupBy={props.groupBy}
      noOptionsText={props.noOptionsText}
      loadingText={props.loadingText}
      renderTags={props.renderTags}
      freeSolo={props.freeSolo}
      clearOnEscape={props.clearOnEscape}
      autoComplete={props.autoComplete}
      popupIcon={props.popupIcon}
      clearIcon={props.clearIcon}
      onKeyDown={props.onKeyDown}
      // InputLabelProps={{
      //     // shrink: props.shrink
      //     classes: {
      //         root: props.bigLabel && classes.bigLabel
      //     },
      // }}
      ListboxComponent={props.listboxComponent}
      sx={{
          '& .MuiAutocomplete-popper': {
              zIndex: 1300,
          },
          ...props.sx
      }}
    />
  );
});

export default AgyleDictionaryField;

AgyleDictionaryField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.any,
    inputValue: PropTypes.any,
    onChange: PropTypes.func,
    options: PropTypes.array.isRequired,
    isMultiple: PropTypes.bool,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    shrink: PropTypes.bool,
    disableCloseOnSelect: PropTypes.bool,
    limitTags: PropTypes.number,
    getOptionLabel: PropTypes.any,
    renderInput: PropTypes.any,
    renderLocalOption: PropTypes.func,
    isOptionEqualToValue: PropTypes.func,
    filterOptions: PropTypes.func,
    withBackground: PropTypes.bool,
    withNewBackground: PropTypes.bool,
    paperBackground: PropTypes.bool,
    size: PropTypes.oneOf(["small", "medium", "large"]),
    textFieldStyle: PropTypes.any,
    style: PropTypes.object,
    placeholder: PropTypes.string,
    noBorder: PropTypes.bool,
    error: PropTypes.bool,
    readOnly: PropTypes.bool,
    freeSolo: PropTypes.bool,
    clearOnEscape: PropTypes.bool,
    autoComplete: PropTypes.bool,
    bigLabel: PropTypes.bool,
    openOnFocus: PropTypes.bool,
    focusInput: PropTypes.func,
    disableClearable: PropTypes.bool,
    dropdownStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    disablePortal: PropTypes.bool,
    optionStyle: PropTypes.any
};

AgyleDictionaryField.defaultProps = {
    label: "",
    value: null,
    isMultiple: false,
    disabled: false,
    loading: false,
    shrink: false,
    disableCloseOnSelect: false,
    error: false,
    getOptionLabel: null,
    isOptionEqualToValue: null,
    size: "small",
    paperBackground: false,
    withBackground: true,
    withNewBackground: false,
    textFieldStyle: null,
    placeholder: "",
    noBorder: true,
    readOnly: false,
    freeSolo: false,
    clearOnEscape: false,
    autoComplete: false,
    bigLabel: false,
    openOnFocus: false,
    disableClearable: false,
    disablePortal: false,
};